import React, { useRef, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/utils/cn";

interface TruncatedTextProps extends React.HTMLAttributes<HTMLElement> {
  /** 要显示的文本内容 */
  text: string;
  /** 自定义类名 */
  className?: string;
  /** tooltip的自定义类名 */
  tooltipClassName?: string;
  /** 是否禁用tooltip */
  disableTooltip?: boolean;
  /** 作为什么HTML元素渲染 */
  as?: "p" | "div" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  /** tooltip的位置 */
  tooltipSide?: "top" | "right" | "bottom" | "left";
  /** tooltip挂载的容器，默认为document.body */
  portalContainer?: HTMLElement;
  /** 移动端tooltip显示时长（毫秒），默认2000ms */
  mobileTooltipDuration?: number;
  /** 桌面端tooltip显示延迟（毫秒），默认1000ms */
  desktopTooltipDelay?: number;
}

export function TruncatedText({
  text,
  className,
  tooltipClassName,
  disableTooltip = false,
  as: Component = "p",
  tooltipSide = "bottom",
  portalContainer,
  mobileTooltipDuration = 2000,
  desktopTooltipDelay = 1000,
  ...htmlProps
}: TruncatedTextProps) {
  const textRef = useRef<
    HTMLParagraphElement | HTMLDivElement | HTMLHeadingElement
  >(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const checkTruncation = () => {
      if (textRef.current) {
        const element = textRef.current;
        // 检查是否发生了截断
        const truncated = element.scrollWidth > element.clientWidth;
        setIsTruncated(truncated);
      }
    };

    // 初始检查
    checkTruncation();

    // 监听窗口大小变化
    window.addEventListener("resize", checkTruncation);

    // 使用 ResizeObserver 监听元素大小变化（更精确）
    let resizeObserver: ResizeObserver | null = null;
    if (textRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(checkTruncation);
      resizeObserver.observe(textRef.current);
    }

    return () => {
      window.removeEventListener("resize", checkTruncation);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
    };
  }, [text]);

  // 检测是否为移动设备
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    ) || ('ontouchstart' in window);
  };

  // 计算tooltip位置
  const calculateTooltipPosition = () => {
    if (!textRef.current) return;

    const rect = textRef.current.getBoundingClientRect();
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    let x = 0;
    let y = 0;

    switch (tooltipSide) {
      case "top":
        x = rect.left + scrollX + rect.width / 2;
        y = rect.top + scrollY - 10; // 10px offset
        break;
      case "bottom":
        x = rect.left + scrollX + rect.width / 2;
        y = rect.bottom + scrollY + 10;
        break;
      case "left":
        x = rect.left + scrollX - 10;
        y = rect.top + scrollY + rect.height / 2;
        break;
      case "right":
        x = rect.right + scrollX + 10;
        y = rect.top + scrollY + rect.height / 2;
        break;
    }

    setTooltipPosition({ x, y });
  };

  // 显示tooltip
  const showTooltipHandler = () => {
    if (isTruncated && !disableTooltip) {
      calculateTooltipPosition();
      setShowTooltip(true);
      
      // 清除之前的定时器
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      
      // 移动端自动隐藏tooltip
      if (isMobile()) {
        hideTimeoutRef.current = setTimeout(() => {
          setShowTooltip(false);
        }, mobileTooltipDuration);
      }
    }
  };

  // 隐藏tooltip
  const hideTooltipHandler = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
    }
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
    }
    setShowTooltip(false);
  };

  // 处理鼠标进入（桌面端）
  const handleMouseEnter = (e: React.MouseEvent<HTMLElement>) => {
    if (!isMobile()) {
      // 清除之前的定时器
      if (showTimeoutRef.current) {
        clearTimeout(showTimeoutRef.current);
      }
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      
      // 延迟显示tooltip
      showTimeoutRef.current = setTimeout(() => {
        showTooltipHandler();
      }, desktopTooltipDelay);
    }
    // 调用用户传入的onMouseEnter事件
    if (htmlProps.onMouseEnter) {
      htmlProps.onMouseEnter(e);
    }
  };

  // 处理鼠标离开（桌面端）
  const handleMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    if (!isMobile()) {
      hideTooltipHandler();
    }
    // 调用用户传入的onMouseLeave事件
    if (htmlProps.onMouseLeave) {
      htmlProps.onMouseLeave(e);
    }
  };

  // 处理触摸开始（移动端）
  const handleTouchStart = (e: React.TouchEvent<HTMLElement>) => {
    if (isMobile()) {
      showTooltipHandler();
    }
    // 调用用户传入的onTouchStart事件
    if (htmlProps.onTouchStart) {
      htmlProps.onTouchStart(e);
    }
  };

  // 处理点击事件（移动端备用方案）
  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isMobile() && isTruncated && !disableTooltip) {
      e.preventDefault();
      if (showTooltip) {
        hideTooltipHandler();
      } else {
        showTooltipHandler();
      }
    }
    // 调用用户传入的onClick事件
    if (htmlProps.onClick) {
      htmlProps.onClick(e);
    }
  };

  // 处理全局点击事件（移动端隐藏tooltip）
  useEffect(() => {
    const handleGlobalClick = (e: Event) => {
      if (isMobile() && showTooltip && textRef.current && !textRef.current.contains(e.target as Node)) {
        hideTooltipHandler();
      }
    };

    if (showTooltip) {
      document.addEventListener('click', handleGlobalClick);
      document.addEventListener('touchstart', handleGlobalClick);
    }

    return () => {
      document.removeEventListener('click', handleGlobalClick);
      document.removeEventListener('touchstart', handleGlobalClick);
    };
  }, [showTooltip]);

  // 根据tooltip位置生成三角形组件
  const renderTriangle = (side: string) => {
    const triangleStyles = {
      top: "absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent border-t-[rgb(16,24,40)]",
      bottom: "absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-[rgb(16,24,40)]",
      left: "absolute left-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-b-[6px] border-l-[6px] border-t-transparent border-b-transparent border-l-[rgb(16,24,40)]",
      right: "absolute right-full top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-b-[6px] border-r-[6px] border-t-transparent border-b-transparent border-r-[rgb(16,24,40)]"
    };

    return (
      <div className={triangleStyles[side as keyof typeof triangleStyles]} />
    );
  };

  // 获取tooltip的transform样式
  const getTooltipTransform = () => {
    switch (tooltipSide) {
      case "top":
        return "translate(-50%, -100%)";
      case "bottom":
        return "translate(-50%, 0%)";
      case "left":
        return "translate(-100%, -50%)";
      case "right":
        return "translate(0%, -50%)";
      default:
        return "translate(-50%, -100%)";
    }
  };

  // 渲染Portal tooltip
  const renderTooltip = () => {
    if (!showTooltip || !isTruncated || disableTooltip) return null;

    const tooltipContent = (
      <div
        className={cn(
          "fixed z-[9999] pointer-events-none",
          tooltipClassName
        )}
        style={{
          left: tooltipPosition.x,
          top: tooltipPosition.y,
          transform: getTooltipTransform(),
        }}
      >
        <div className="relative">
          <p className="px-3 py-2 rounded-sm bg-[rgb(16,24,40)] max-w-xs text-xs text-white break-words whitespace-normal">
            {text}
          </p>
          {/* 使用单独的标签渲染三角形指示器 */}
          {renderTriangle(tooltipSide)}
        </div>
      </div>
    );

    // 使用Portal挂载到指定容器或body
    const container = portalContainer || document.body;
    return createPortal(tooltipContent, container);
  };

  const textElement = (
    <Component
      ref={textRef}
      className={cn("truncate", className)}
      {...htmlProps}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleTouchStart}
      onClick={handleClick}
    >
      {text}
      {/** 兼容 safari 在safari 假设元素只有文本阶段默认会触发一个tooltip 所以通过一个div元素占位来屏蔽这个机制 */}
      {isTruncated && <div></div>}
    </Component>
  );

  return (
    <>
      {textElement}
      {renderTooltip()}
    </>
  );
}
